// Charts for the Incoming / Outgoing Summary pages. Both pages load every summary
// row for the date range in one response (no server paging) and filter on the
// client, so the charts are built from exactly the rows the table shows.
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  ColumnChart,
  DonutChart,
  HBarChart,
  REPORT_COLORS,
  ReportChartCard,
  TopNSelect,
} from '@/components/charts/ReportCharts';

interface SummaryRow {
  did?: string | number | null;
  schedule_date?: string | null;
  nos_processed?: string | number | null;
  connected_calls?: string | number | null;
  billing_duration?: string | number | null;
}

const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// schedule_date is stored in UTC; the table shows it in IST — bucket days the same way.
const istDayKey = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
};
const dayLabel = (key: string) => {
  const [y = 0, m = 1, d = 1] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

export default function SummaryCharts({ rows, kind }: { rows: SummaryRow[]; kind: 'incoming' | 'outgoing' }) {
  const [show, setShow] = useState(false);
  // How many DIDs the "Calls by DID" chart plots; its height grows with it. 0 = all.
  const [topDids, setTopDids] = useState(8);
  const TOP_DIDS = topDids || Infinity;

  const data = useMemo(() => {
    let processed = 0;
    let connected = 0;
    let billing = 0;
    const byDay = new Map<string, { processed: number; connected: number; billing: number }>();
    const byDid = new Map<string, { processed: number; connected: number }>();

    rows.forEach((r) => {
      const p = num(r.nos_processed);
      const c = Math.min(num(r.connected_calls), p || num(r.connected_calls));
      const b = num(r.billing_duration);
      processed += p;
      connected += c;
      billing += b;

      const day = istDayKey(r.schedule_date);
      if (day) {
        const cur = byDay.get(day) || { processed: 0, connected: 0, billing: 0 };
        cur.processed += p;
        cur.connected += c;
        cur.billing += b;
        byDay.set(day, cur);
      }

      const did = String(r.did ?? '').trim() || 'Unknown';
      const curDid = byDid.get(did) || { processed: 0, connected: 0 };
      curDid.processed += p;
      curDid.connected += c;
      byDid.set(did, curDid);
    });

    const daily = [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, v]) => ({
        day: dayLabel(key),
        connected: v.connected,
        notConnected: Math.max(0, v.processed - v.connected),
        billingMin: Math.round(v.billing / 60),
      }));

    const dids = [...byDid.entries()]
      .sort((a, b) => b[1].processed - a[1].processed)
      .slice(0, TOP_DIDS)
      .map(([did, v]) => ({
        did,
        connected: v.connected,
        notConnected: Math.max(0, v.processed - v.connected),
      }));

    return {
      processed,
      connected,
      billing,
      outcome: [
        { name: 'Connected', value: connected, color: REPORT_COLORS.good },
        { name: 'Not connected', value: Math.max(0, processed - connected), color: REPORT_COLORS.crit },
      ],
      daily,
      dids,
      didCount: byDid.size,
    };
  }, [rows, TOP_DIDS]);

  if (!rows.length) return null;

  const connectRate = data.processed ? `${((data.connected / data.processed) * 100).toFixed(1)}%` : '0%';
  const billingMinutes = Math.round(data.billing / 60).toLocaleString('en-IN');
  const label = kind === 'incoming' ? 'Incoming' : 'Outgoing';
  // Few days -> the two daily charts sit side by side; a longer range gets full width each.
  const dailyWide = data.daily.length < 5 ? '' : 'lg:col-span-2';
  const stackSeries = [
    { key: 'connected', label: 'Connected', color: REPORT_COLORS.good },
    { key: 'notConnected', label: 'Not connected', color: REPORT_COLORS.crit },
  ];

  return (
    <>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label} charts
        </span>
        <Button variant="ghost" size="sm" onClick={() => setShow((v) => !v)}>
          {show ? 'Hide charts' : 'Show charts'}
        </Button>
      </div>
      {show ? (
        <div className="mb-4 grid gap-3 lg:grid-cols-2">
          <ReportChartCard
            title="Calls connected"
            subtitle={`Connect rate ${connectRate} · ${billingMinutes} billing min`}
          >
            <DonutChart data={data.outcome} centerLabel="Numbers" emptyLabel="No calls in this range" />
          </ReportChartCard>
          <ReportChartCard
            title="Calls by DID"
            subtitle={data.didCount > TOP_DIDS ? `Top ${TOP_DIDS} of ${data.didCount} DIDs` : 'All DIDs in view'}
            actions={<TopNSelect value={topDids} onChange={setTopDids} label="DIDs" />}
          >
            <HBarChart
              data={data.dids}
              categoryKey="did"
              stacked
              rowHeight={26}
              series={stackSeries}
              emptyLabel="No DIDs in this range"
            />
          </ReportChartCard>
          <ReportChartCard title="Daily trend" subtitle="Numbers processed per day" className={dailyWide}>
            <ColumnChart
              data={data.daily}
              categoryKey="day"
              stacked
              series={stackSeries}
              emptyLabel="No calls in this range"
            />
          </ReportChartCard>
          <ReportChartCard title="Billing minutes per day" subtitle="Billing duration, in minutes" className={dailyWide}>
            <ColumnChart
              data={data.daily}
              categoryKey="day"
              series={[{ key: 'billingMin', label: 'Billing minutes', color: REPORT_COLORS.call }]}
              emptyLabel="No billing in this range"
            />
          </ReportChartCard>
        </div>
      ) : null}
    </>
  );
}
